import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import Pane from 'components/Common/Pane'
import config from 'constants/config'
import isTimeLimited from 'lib/offer/isTimeLimited'
import OfferTimeLimitedText from '../OfferTimeLimitedText'

import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { OFFER_TYPE_LAST_MINUTE_SLUG } from 'constants/offer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import { OfferPackageOptionView, getCheapestSelectedPackageOption, getCheapestAvailablePackageOption, areCalendarsLoading } from 'selectors/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LastMinuteDiscountText from './LastMinuteDiscountText'
import TextButton from 'components/Luxkit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'components/Luxkit/TextLink'
import WalledContent from 'components/Common/WalledContent'
import OfferFromDurationLabel from '../OfferFromDurationLabel'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import InlineGroup from 'components/Common/InlineGroup'
import Heading from 'components/Luxkit/Typography/Heading'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import { useBusinessTravellerStickyBudgetBarHeightContext } from 'businessTraveller/utils/useBusinessBudgetBarHeightContext'
import Group from 'components/utils/Group'

const SummaryPane = styled(Pane)`
  padding: ${rem(28)} ${rem(20)};
  position: relative;
  border: 1px solid ${({ theme }) => theme.palette.neutral.six};
  border-radius: ${props => props.theme.borderRadius.small};

  > * + * {
    margin-top: ${rem(12)};
  }
`

const PackagePrices = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(4)};
`

const FlightPrice = styled(PriceDetails)`
  margin-top: ${rem(8)};
`

const TaxAndFeesWrapper = styled.div`
  display: grid;
  text-align: center;
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  text-align: center;
`

interface Props {
  offer: App.Offer;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  rooms: Array<App.Occupants>;
  className?: string;
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  onViewPackageOptions: () => void;
  calendarsLoading?: boolean;
}

function LastMinuteSummary(props: Props) {
  const {
    className,
    onViewPackageOptions,
    offer,
    flightOrigin,
    cheapestSelectedOption,
    cheapestOption,
    rooms,
    flightDestination,
    calendarsLoading,
  } = props

  const offerDispatch = useOfferPageDispatch()
  const packageView = cheapestSelectedOption ?? cheapestOption

  const {
    flightPrice,
    package: pkg,
  } = packageView

  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const availableForPurchase = useIsAvailableForPurchase()
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const priceTextColour = useHotelPriceColour(packageView.taxesAndFees)

  const onToggleFilter = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerDispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
  }

  const businessTravellerStickyBudgetBarStyle = useBusinessTravellerStickyBudgetBarHeightContext()

  return (
    <SummaryPane style={businessTravellerStickyBudgetBarStyle} type="clean" shadow className={className} data-testid="SummaryPane">
      {availableForPurchase && <>
        {isTimeLimited(offer) && <OfferTimeLimitedText />}
        <WalledContent enforced={offer.walledGarden} wall={(
          <BodyText variant="large" weight="bold">{offer.durationLabel} packages available</BodyText>
        )}>
          {showPrices && (
            <PackagePrices>
              <PriceDetails>
                <OfferFromDurationLabel variant="medium" />
                <InlineGroup vertical="center" data-testid="pricing">
                  <Heading variant="heading3" as="span" colour={priceTextColour} >
                    <FormatCurrency value={packageView.hotelPrice} />
                  </Heading>
                  <BodyText as="span" variant="large" weight="semi-bold">/{offer.saleUnit}</BodyText>
                </InlineGroup>
                <TaxAndFeesWrapper>
                  <TaxesAndFees
                    taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
                    taxAmount={packageView.taxesAndFees}
                  />
                </TaxAndFeesWrapper>
                {cheapestOption.package.discountPercent > 0 &&
                  <LastMinuteDiscountText discountPercent={cheapestOption.package.discountPercent} />
                }
                <Group direction="vertical" horizontalAlign="center" verticalAlign="center" gap={4}>
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                    complex={false}
                    description="+ Extra guest surcharge"
                    modalContent={pkg.roomPolicyDescription}
                  />
                  )}
                  {flightsEnabled &&
                    <FlightPrice>
                      {!hasFlights && flightOrigin.code === flightDestination?.code && (
                        <ChangeDepartureWrapper>
                          <Caption variant="large" colour="neutral-three">
                            No flights available from {flightOrigin.name}. Please select another airport.
                          </Caption>
                          <TextLink
                          onClick={onToggleFilter}
                          data-filter="airport"
                        >
                            Change departure airport
                          </TextLink>
                        </ChangeDepartureWrapper>
                      )}
                      {!hasFlights && flightOrigin.code !== flightDestination?.code && (
                        <Caption variant="large" colour="neutral-three" align="center">
                          Add flights to the hotel package. Select from all
                          major airlines.
                        </Caption>
                      )}
                      {hasFlights &&
                        <InlineGroup vertical="center" data-testid="FlightPrice">
                          {/* TODO: When translating this, use the HotelPlusFlightsFrom component here */}
                          <LinePlaneFlyIcon />&nbsp;
                          <Caption variant="medium">Hotel + flights from&nbsp;</Caption>
                          <Caption variant="medium" colour={priceTextColour} weight="bold">
                            <FormatCurrency value={packageView.hotelPrice + packageView.flightPrice} />*&nbsp;
                          </Caption>
                          {!rooms && <Caption variant="medium" colour="neutral-three">
                            (Two Adults)
                          </Caption>}
                        </InlineGroup>
                    }
                      <AtolProtectedTooltip />
                    </FlightPrice>
                }
                  {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                  type="estimate"
                  offer={offer}
                  totalCost={packageView.hotelPrice}
                  numberOfNights={pkg.duration}
                />}
                </Group>
              </PriceDetails>
              <LoadingIndicator floating backdrop="grey-light" opaque visible={!!calendarsLoading} />
            </PackagePrices>
          )}
        </WalledContent>
        <TextButton
          kind="primary"
          data-testid="OfferAvailable"
          onClick={onViewPackageOptions}
          fit="flex">
          View {config.brandConfig.travelSynonym} options
        </TextButton>
      </>}
      {!availableForPurchase &&
        <>
          <BodyText
            variant="large"
            data-testid="OfferSoldOut"
            weight="bold"
          >
            This offer has SOLD OUT
          </BodyText>
          <TextButton
            kind="primary"
            to={`/${OFFER_TYPE_LAST_MINUTE_SLUG}`}
            fit="flex">
            Browse all Last Minute Escapes
          </TextButton>
        </>
      }
    </SummaryPane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  rooms: state.rooms,
  offer: state.offer,
  flightOrigin: state.airport,
  calendarsLoading: areCalendarsLoading(appState, state),
}))(LastMinuteSummary)
